import { Box } from "@mui/material";
import dagre from "dagre";
import { useEffect, useRef, useState } from "react";
import ReactFlow, { MarkerType, useEdgesState, useNodesState } from "reactflow";
import useColors from "../../../../api/hooks/useColors";
import useSmartAndRegularAssets from "../../../../api/hooks/useSmartAndRegularAssets";
import LoadingIcon from "../../../../components/LoadingIcon";
import { ProtectionLevelText } from "../../../../utils/constants";
import { isScenarioEffectivelyMitigated } from "../../../../utils/scenarioUtils";
import ScenarioSetEdge from "./ScenarioSetEdge";
import ScenarioSetNode from "./ScenarioSetNode";

const dagreGraph = new dagre.graphlib.Graph();
dagreGraph.setDefaultEdgeLabel(() => ({}));

const nodeWidth = 100;
const nodeHeight = 40;

const getLayoutedElements = (nodes, edges) => {
  // LR = Left to Right
  dagreGraph.setGraph({ rankdir: "LR" });
  const nodeWidthByNodes = nodes.length < 6 ? 250 : nodeWidth;
  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, {
      width: nodeWidthByNodes,
      height: nodeHeight,
    });
  });

  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  nodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);

    if (nodeWithPosition) {
      node.position = {
        x: nodeWithPosition.x - nodeWidthByNodes / 2,
        y: nodeWithPosition.y - nodeHeight / 2,
      };
    }
    if (nodeWithPosition) {
      node.position = {
        x: nodeWithPosition.x - nodeWidthByNodes / 2,
        y: nodeWithPosition.y - nodeHeight / 2,
      };
    }

    return node;
  });
  return { nodes, edges };
};

const nodeTypes = { customNode: ScenarioSetNode };
const edgeTypes = { customEdge: ScenarioSetEdge };

const ScenariosSingleFlow = ({
  scenario,
  scenarioSet,
  fetchedSteps,
  selectedAsset,
  height,
  numberOfScenariosInPage,
}) => {
  const colors = useColors();

  const reactFlowRef = useRef(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [preLayoutNodes, setPreLayoutNodes] = useState(null);
  const [nodesAreLoading, setNodesAreLoading] = useState(true);
  const [preLayoutEdges, setPreLayoutEdges] = useState(null);
  const [loading, setLoading] = useState(true);
  const { regularAndActiveSmartAsset: assets, isLoadingAll: assetsIsLoading } =
    useSmartAndRegularAssets();

  const indirectEdgeTooltip = `This step threatens "${selectedAsset?.name}" because an entity at risk has access to one of its entities.`;
  // It's required to solve ResizeObserver loop limit exceeded error
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 700);

    return () => {
      clearTimeout(timer);
    };
  }, []);
  const indirectStepToAssetConnection = (step) => {
    if (!step.misconfigured_entity_ids || !selectedAsset.entity_ids) {
      return false;
    }

    const hasCommonItem = step.misconfigured_entity_ids.some((id) =>
      selectedAsset.entity_ids.includes(id)
    );

    return !hasCommonItem;
  };

  const createAllNodesFromLocationsPhasesAndAsset = () => {
    const tempNodes = [];
    const location_node_ids = [];

    // Identify mitigated step ID if scenario is effectively mitigated
    let mitigatedPhaseIndex = -1;
    if (isScenarioEffectivelyMitigated(scenario, fetchedSteps)) {
      // Find the first mitigated step in the phases
      const steps = scenario.phases.flatMap((phase) =>
        phase.split("-").filter((step) => Number(step) && fetchedSteps[step])
      );
      let mitigatedStepFound = false;
      for (let i = 0; i < steps.length && !mitigatedStepFound; i++) {
        const step = fetchedSteps[steps[i]];
        if (
          step.is_mitigated &&
          step.protection_level !== ProtectionLevelText.RISK_ACCEPTED
        ) {
          // Find the phase index of the mitigated step
          // It will be used to determine if a location is subsequent to the mitigated step
          mitigatedPhaseIndex = scenario.phases.indexOf(
            scenario.phases.find((phase) => phase.includes(steps[i]))
          );
          mitigatedStepFound = true;
          break;
        }
      }
    }

    // node for each location
    for (let i = 0; i < scenarioSet.locations_in_order.length; i++) {
      tempNodes.push({
        id: `${scenarioSet.locations_in_order[i]}-${i}`,
        type: "location",
        value: scenarioSet.locations_in_order[i].toString(),
        position: "middle",
        isSubsequentNode: mitigatedPhaseIndex > -1 && i > mitigatedPhaseIndex,
      });

      location_node_ids.push(`${scenarioSet.locations_in_order[i]}-${i}`);
    }
    // scenario format: location-step_id-step_id-location-step_id...-asset_id
    if (scenario) {
      let previousIsVirtual = false;
      // Build a string of phases separated by '-' to process steps
      let phasesString = "";
      scenario?.phases.forEach((phase) => {
        phasesString += phase + "-";
      });
      phasesString = phasesString.slice(0, -1); // Remove trailing dash

      // Split the phases string into individual elements
      const steps = phasesString.split("-");

      // Remove the first element as it's Location
      // f.e. "I-2-PE-57-LM-95" -> "2-PE-57-LM-95"
      steps.shift();
      let phaseIndex = 0;

      for (let stepIndex = 0; stepIndex < steps.length; stepIndex++) {
        if (!Number(steps[stepIndex])) {
          // If the value is not a number, it is a phase identifier; move to the next phase
          phaseIndex++;
          continue;
        }

        const step_id = steps[stepIndex];
        const step_data = fetchedSteps[step_id];
        if (!step_data) {
          // Should not happen
          return;
        }
        if (step_data.is_virtual) {
          // Skip this step - we don't draw virtual steps
          previousIsVirtual = true;
          continue;
        }

        // suffix is used to know in which phase in the scenario to draw the step
        const suffix = `${phaseIndex}`;
        const node = {
          position: "middle",
          value: step_data,
          type: "step",
          id: `${step_id};${suffix}`,
        };

        const isScenarioHasOnlyOneStep = steps.length === 1;
        const isFirstStep = stepIndex === 0;
        const isMiddleStep = stepIndex > 0 && stepIndex < steps.length - 1;
        const isLastStep = stepIndex === steps.length - 1;

        if (isScenarioHasOnlyOneStep) {
          // Start -> start location
          node.start = location_node_ids[phaseIndex];

          // End -> asset
          node.end = scenarioSet.asset_id;

          // Add tooltip for indirect asset connection if applicable
          if (indirectStepToAssetConnection(step_data)) {
            node.indirectAssetConnection = true;
          }
        }

        if (isFirstStep) {
          // First step in a multi-step phase

          // Start -> connect to the phase start.
          node.start = location_node_ids[phaseIndex];

          // End -> connect to a location or the following step
          if (Number(steps[stepIndex + 1])) {
            if (fetchedSteps[steps[stepIndex + 1]].is_virtual) {
              // Skip the virtual step as we don't draw it
              // and connect to the next step.
              // Virtual Step always located between two actual Steps.
              node.end = steps[stepIndex + 2].toString() + ";" + suffix;
            } else {
              // The next step is actual step so connect to the next step
              node.end = steps[stepIndex + 1].toString() + ";" + suffix;
            }
          } else {
            // The next element is a location so connect to a location
            node.end = location_node_ids[phaseIndex + 1];
          }
        }

        if (isMiddleStep) {
          // Start -> previous Step
          if (Number(steps[stepIndex - 1])) {
            if (previousIsVirtual) {
              // Connect to the non-virtual step prior to the virtual step
              node.start = steps[stepIndex - 2].toString() + ";" + suffix;
            } else {
              node.start = steps[stepIndex - 1].toString() + ";" + suffix;
            }
          } else {
            // Use phase start location if previous item is not a step
            node.start = location_node_ids[phaseIndex];
          }

          // End -> next Step
          if (Number(steps[stepIndex + 1])) {
            if (fetchedSteps[steps[stepIndex + 1]].is_virtual) {
              // Connect to the non-virtual step after to the virtual step
              node.end = steps[stepIndex + 2].toString() + ";" + suffix;
            } else {
              node.end = steps[stepIndex + 1].toString() + ";" + suffix;
            }
          } else {
            // Use phase end location if next item is not a step
            node.end = location_node_ids[phaseIndex + 1];
          }
        }

        if (isLastStep) {
          // Last step in the scenario

          // Start -> connect to the previous step
          if (Number(steps[stepIndex - 1])) {
            if (previousIsVirtual) {
              node.start = steps[stepIndex - 2].toString() + ";" + suffix;
            } else {
              node.start = steps[stepIndex - 1].toString() + ";" + suffix;
            }
          } else {
            node.start = location_node_ids[phaseIndex];
          }

          // End -> connect to the asset
          node.end = scenarioSet.asset_id;

          if (indirectStepToAssetConnection(step_data)) {
            node.indirectAssetConnection = true;
          }
        }

        tempNodes.push(node);
        previousIsVirtual = false;
      }
      // Filter and get asset data for the scenario set's asset id
      const asset = assets?.filter((asset) => {
        return asset?.asset_id === scenarioSet.asset_id;
      })[0];

      // Create a node for the asset
      tempNodes.push({
        id: scenarioSet.asset_id,
        type: "asset",
        value: {
          name: asset?.name,
          type: asset?.type,
          id: scenarioSet.asset_id,
        },
        position: "middle",
      });
    }
    return tempNodes;
  };
  const nodesAndEdgesGetter = () => {
    const nodesAndEdges = { nodes: [], edges: [] };
    let mergedNodes = [];
    mergedNodes = createAllNodesFromLocationsPhasesAndAsset();

    const scenarioSetNodes = mergedNodes?.map((node) => ({
      id: node.id.toString(),
      data: {
        nodeLocation: node.position,
        nodeValue: node.value,
        type: node.type,
        start: node.start,
        end: node.end,
        entity_type_filter: node?.entity_type_filter,
        isSubsequentNode: node?.isSubsequentNode,
        graphSource: "scenarioSetGraph",
        ...(node?.indirectAssetConnection === true
          ? { tooltip: indirectEdgeTooltip }
          : {}),
      },
    }));

    const scenarioSetEdges = [];
    // for every node, there is a start and end node
    // except for the asset node which only has a start node
    // for every node, create an edge that connects it to the start and end nodes
    // if they exist
    for (let i = 0; i < mergedNodes.length; i++) {
      const location = mergedNodes[i]?.id.toString();
      const startLocation = mergedNodes[i]?.start;
      const endLocation = mergedNodes[i]?.end;

      if (startLocation) {
        const edge = {
          id: `e${startLocation}-${location}`,
          source: startLocation,
          target: location,
          markerEnd: {
            type: MarkerType.Arrow,
            width: 5,
            height: 5,
            color: colors.primary_32percent,
          },
        };
        scenarioSetEdges.push(edge);
      }

      if (endLocation) {
        const edge = {
          id: `e${location}-${endLocation}`,
          source: location,
          target: endLocation,
          markerEnd: {
            type: MarkerType.Arrow,
            width: 5,
            height: 5,
            color: colors.primary_32percent,
          },
          style:
            mergedNodes[i]?.indirectAssetConnection === true
              ? { strokeDasharray: "5,5" } // Dashed line
              : {},
          data:
            mergedNodes[i]?.indirectAssetConnection === true
              ? { tooltip: indirectEdgeTooltip }
              : {},
        };
        scenarioSetEdges.push(edge);
      }
    }
    let scenarioSetNodesSet = new Set(scenarioSetNodes.map((node) => node.id));
    const uniqueScenarioSetNodes = Array.from(scenarioSetNodesSet).map((id) =>
      scenarioSetNodes.find((node) => node.id === id)
    );
    let scenarioSetEdgesSet = new Set(scenarioSetEdges.map((edge) => edge.id));
    const uniqueScenarioSetEdges = Array.from(scenarioSetEdgesSet).map((id) =>
      scenarioSetEdges.find((edge) => edge.id === id)
    );

    nodesAndEdges.nodes.push(...uniqueScenarioSetNodes);
    nodesAndEdges.edges.push(...uniqueScenarioSetEdges);

    return nodesAndEdges;
  };

  useEffect(() => {
    if (scenarioSet && scenario && !assetsIsLoading) {
      if (!assets) return;
      const result = nodesAndEdgesGetter();

      // Find the mitigated step - it's the first step in an effectively mitigated scenario that has is_mitigated set to true and is not RISK_ACCEPTED
      const mitigatedNode = isScenarioEffectivelyMitigated(
        scenario,
        fetchedSteps
      )
        ? result.nodes.find(
            (node) =>
              node.data.nodeValue.is_mitigated === true &&
              node.data.nodeValue.protection_level !==
                ProtectionLevelText.RISK_ACCEPTED
          )
        : null;

      const nodesWithTypes = result.nodes.map((node) => {
        // Find the current node's position in the flow
        const flowNode = result.nodes.find((n) => n.id === node.id);
        const currentFlowIndex = flowNode ? flowNode.flowIndex : -1;

        // A node is subsequent if there is a mitigated step and it's after the mitigated step
        const isSubsequentNode =
          node.data.isSubsequentNode ||
          (mitigatedNode &&
            result.nodes.findIndex((n) => n.id === node.id) >
              result.nodes.findIndex(
                (n) =>
                  n.id === mitigatedNode.id && n.flowIndex === currentFlowIndex
              ));
        return {
          ...node,
          type: "customNode",
          data: {
            ...node.data,
            isScenarioMitigated: isScenarioEffectivelyMitigated(
              scenario,
              fetchedSteps
            ),
            isSubsequentNode: isSubsequentNode,
          },
        };
      });
      setPreLayoutNodes(nodesWithTypes);

      const edgesWithTypes = result.edges.map((edge) => {
        // Find source and target nodes
        const edgeSourceNode = result.nodes.find(
          (node) => node.id === edge.source
        );
        const edgeTargetNode = result.nodes.find(
          (node) => node.id === edge.target
        );

        if (!edgeSourceNode || !edgeTargetNode) {
          return {
            ...edge,
            type: "customEdge",
            data: { ...edge.data },
          };
        }

        // An edge is mitigated if it starts from the mitigated step
        const isMitigatedEdge = edgeSourceNode.id === mitigatedNode?.id;

        // An edge is subsequent if it connects nodes after the mitigated step
        const sourceIsAfterMitigated =
          result.nodes.findIndex((n) => n.id === edgeSourceNode.id) >
          result.nodes.findIndex((n) => n.id === mitigatedNode?.id);
        const targetIsAfterMitigated =
          result.nodes.findIndex((n) => n.id === edgeTargetNode.id) >
          result.nodes.findIndex((n) => n.id === mitigatedNode?.id);

        const isSubsequentEdge =
          sourceIsAfterMitigated || targetIsAfterMitigated;

        return {
          ...edge,
          style: {
            stroke:
              isSubsequentEdge || isMitigatedEdge
                ? colors.grey_300
                : colors.grey_500,
            strokeWidth: 1.5,
            strokeOpacity: 2,
          },
          data: {
            ...edge.data,
            isMitigated: isMitigatedEdge,
          },
          type: "customEdge",
        };
      });
      setPreLayoutEdges(edgesWithTypes);
    }
    setNodesAreLoading(false);
  }, [scenarioSet, scenario, assetsIsLoading, assets, fetchedSteps]);

  useEffect(() => {
    if (preLayoutNodes && preLayoutEdges) {
      const { nodes: layoutedNodes, edges: layoutedEdges } =
        getLayoutedElements(preLayoutNodes, preLayoutEdges);

      const start_asset_y_value = layoutedNodes[0].position.y;
      const newLayoutedNodes = layoutedNodes.map((node) => ({
        ...node,
        position: {
          x: node.position.x,
          y: start_asset_y_value,
        },
      }));

      setNodes(newLayoutedNodes);
      setEdges(layoutedEdges);
    }
  }, [nodesAreLoading]);

  return (
    <div>
      <Box
        sx={{
          height: height / numberOfScenariosInPage,
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          border: isScenarioEffectivelyMitigated(scenario, fetchedSteps)
            ? `1px solid ${colors.success_48percent}`
            : "none",
          borderRadius: "10px",
        }}
      >
        {loading ? (
          <LoadingIcon />
        ) : (
          <ReactFlow
            ref={reactFlowRef}
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            fitView
            fitViewOptions={{
              padding: 0.8,
            }}
            style={{
              backgroundColor: colors.common_white,
              boxShadow: "0px 0px 4px 0px " + colors.grey_24percent,
              borderRadius: "10px",
            }}
            proOptions={{ hideAttribution: true }}
          ></ReactFlow>
        )}
      </Box>
    </div>
  );
};

export default ScenariosSingleFlow;
